import { Component, Input } from '@angular/core';
import { FormControl } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { EnumValues } from '@card-software/library';
import _ from 'lodash';
import {
  FinishedGoodsBalance,
  ProductionCard,
  ProductionCardBalance,
  ProductionCardStatus,
  WeightUom,
  WeightUomOption,
} from '@app/models/production/production-card';
import { ProductionCardBundle, ProductionCardBundleStatus } from '@app/models/production/production-card/production-card-bundle';
import { Roll } from '@app/models/knitting/roll';
import { Fabric } from '@app/models/order/work-order';
import { ColumnOption } from '@app/models/common';
import { EnumService } from '@app/services/enum.service';
import { KnittingUtil } from '@app/utilities/knitting-util';

export type viewMode = 'production-card' | 'rolls' | 'finished-goods';

@Component({
  selector: 'app-fabric-summary-table',
  templateUrl: './fabric-summary-table.component.html',
  styleUrl: './fabric-summary-table.component.scss',
})
export class FabricSummaryTableComponent {
  @Input() viewMode: viewMode = 'production-card';
  @Input() productionCard: ProductionCard | null = null;
  @Input() rolls: Roll[] = [];
  @Input() fabrics: Fabric[] = [];
  @Input() showFabricsWithoutRolls = false;
  @Input() loadedRollNumbers: string[] = [];
  @Input() bundles: ProductionCardBundle[] = [];
  @Input() readOnly = false;

  fabricFormValues: EnumValues;

  // Column visibility control
  selectedColumnsControl = new FormControl<string[]>([]);

  // UOM selector
  selectedUom: WeightUom = 'lb';

  constructor(
    enumService: EnumService,
    private translate: TranslateService,
  ) {
    this.fabricFormValues = enumService.getEnum('FabricForm', 'itemEnums')?.values || {};
  }

  // Get available optional columns with translated headers
  get availableOptionalColumns(): ColumnOption[] {
    return [{ field: 'plannedBalance', header: this.translate.instant('Fabric_Summary_Table_PlannedBalance') }];
  }

  // Get UOM options with translated labels
  get uomOptions(): WeightUomOption[] {
    return [
      { label: this.translate.instant('Fabric_Summary_Table_PoundsLb'), value: 'lb' },
      { label: this.translate.instant('Fabric_Summary_Table_KilogramsKg'), value: 'kg' },
      { label: this.translate.instant('Fabric_Summary_Table_YardsYd'), value: 'yd' },
    ];
  }

  // Get UOM suffix for column headers
  get uomSuffix(): string {
    return this.selectedUom;
  }

  getProductionCardBalanceStrict(item: unknown): ProductionCardBalance {
    return item as ProductionCardBalance;
  }

  // Check if production card is at or after batching status
  get isAtOrAfterBatching(): boolean {
    if (!this.productionCard) return false;
    const batchingStatuses = [
      ProductionCardStatus.Batching,
      ProductionCardStatus.InRouteToBrd,
      ProductionCardStatus.Brd,
      ProductionCardStatus.Dyeing,
      ProductionCardStatus.ReadyForFinishing,
      ProductionCardStatus.Finishing,
      ProductionCardStatus.FinishedGood,
      ProductionCardStatus.Shipped,
    ];
    return batchingStatuses.includes(this.productionCard.status);
  }

  // Check if any bundle has been weighed
  get hasBundleWeights(): boolean {
    return this.bundles.some((bundle) => bundle.status >= ProductionCardBundleStatus.Weighed && (bundle.actualWeightInLbs ?? 0) > 0);
  }

  // Get visible columns based on status and user selection
  get visibleColumns(): string[] {
    const baseColumns = ['fabric', 'form'];

    if (this.viewMode === 'production-card') {
      // Always show Planned Balance before batching
      if (!this.isAtOrAfterBatching) {
        baseColumns.push('plannedBalance');
      } else {
        // After batching, only show if user selected it
        if (this.selectedColumnsControl.value?.includes('plannedBalance')) {
          baseColumns.push('plannedBalance');
        }
      }

      // Always show Transferred Quantity (roll-based)
      baseColumns.push('transferredQuantity');

      // Show Bundle Weight Balance column only for total in footer (not per-fabric breakdown)
      // Since bundles weigh multiple rolls together, we cannot accurately determine per-fabric weights
      if (this.hasBundleWeights) {
        baseColumns.push('bundleWeightBalance');
      }

      baseColumns.push('rollCount');
    } else if (this.viewMode === 'rolls') {
      baseColumns.push('transferredQuantity');

      // Show Bundle Weight Balance column only for total in footer
      if (this.hasBundleWeights) {
        baseColumns.push('bundleWeightBalance');
      }

      baseColumns.push('rollCount');
    } else {
      baseColumns.push('transferredQuantity', 'rollCount');
    }

    return baseColumns;
  }

  // Show column selector only after batching
  get showColumnSelector(): boolean {
    return this.isAtOrAfterBatching && this.viewMode === 'production-card';
  }

  get productionCardPlannedBalances(): ProductionCardBalance[] {
    const balancesMap: Record<string, ProductionCardBalance> = {};

    // Calculate bundle weight per fabric code
    const bundleWeightByFabric = this.calculateBundleWeightByFabric();

    if (_.isEmpty(this.rolls) && !_.isNil(this.productionCard) && this.viewMode === 'production-card') {
      this.productionCard.productionCardEntries.forEach((entry) => {
        const fabric = this.getFabric(entry.fabricCode);
        const longDescription = fabric?.longDescription || 'Unknown Fabric';
        const type = !_.isNil(fabric?.fabricForm) ? this.fabricFormValues[fabric.fabricForm] : 'Unknown';

        balancesMap[entry.fabricCode] = {
          fabricCode: entry.fabricCode,
          longDescription,
          type,
          planned: entry.quantity,
          currentBalance: 0,
          rollCount: 0,
          bundleWeightBalance: bundleWeightByFabric[entry.fabricCode] || 0,
        };
      });

      return Object.values(balancesMap);
    }

    this.rolls.forEach((roll) => {
      const fabricCode = roll.expectedFabricCode!;
      const fabric = this.getFabric(fabricCode);
      const longDescription = fabric?.longDescription || 'Unknown Fabric';
      const type = !_.isNil(fabric?.fabricForm) ? this.fabricFormValues[fabric.fabricForm] : 'Unknown';
      const weight = !_.isNil(roll.overrideWeight) ? roll.overrideWeight : KnittingUtil.getRollWeightInLbs(roll);

      if (!balancesMap[fabricCode]) {
        let plannedBalance = 0;

        if (!_.isNil(this.productionCard)) {
          const productionCardEntry = this.productionCard.productionCardEntries.find((entry) => entry.fabricCode === fabricCode);
          if (productionCardEntry) {
            plannedBalance = productionCardEntry.quantity;
          }
        }

        balancesMap[fabricCode] = {
          fabricCode,
          longDescription,
          type,
          planned: plannedBalance,
          currentBalance: 0,
          rollCount: 0,
          bundleWeightBalance: bundleWeightByFabric[fabricCode] || 0,
        };
      }

      balancesMap[fabricCode].currentBalance += weight;
      balancesMap[fabricCode].rollCount += 1;
    });

    // If showFabricsWithoutRolls is true, add fabrics from production card that have no rolls
    if (this.showFabricsWithoutRolls && !_.isNil(this.productionCard)) {
      this.productionCard.productionCardEntries.forEach((entry) => {
        if (!balancesMap[entry.fabricCode]) {
          const fabric = this.getFabric(entry.fabricCode);
          const longDescription = fabric?.longDescription || 'Unknown Fabric';
          const type = !_.isNil(fabric?.fabricForm) ? this.fabricFormValues[fabric.fabricForm] : 'Unknown';

          balancesMap[entry.fabricCode] = {
            fabricCode: entry.fabricCode,
            longDescription,
            type,
            planned: entry.quantity,
            currentBalance: 0,
            rollCount: 0,
            bundleWeightBalance: bundleWeightByFabric[entry.fabricCode] || 0,
          };
        }
      });
    }

    return Object.values(balancesMap);
  }

  // Calculate bundle weight grouped by fabric code
  // Note: Since bundles weigh rolls together, we can only show total bundle weight
  // We cannot guarantee per-fabric accuracy after bundle weighing
  private calculateBundleWeightByFabric(): Record<string, number> {
    const result: Record<string, number> = {};

    // Get only weighed bundles
    const weighedBundles = this.bundles.filter(
      (bundle) => bundle.status >= ProductionCardBundleStatus.Weighed && (bundle.actualWeightInLbs ?? 0) > 0,
    );

    if (weighedBundles.length === 0) {
      return result;
    }

    // For each weighed bundle, distribute weight proportionally based on roll fabric codes
    weighedBundles.forEach((bundle) => {
      const bundleWeight = bundle.actualWeightInLbs ?? 0;
      const bundleRolls = bundle.rolls || [];

      if (bundleRolls.length === 0) {
        // If no rolls detail, we cannot distribute by fabric
        // In this case, we'll just add to a generic total
        return;
      }

      // Calculate total expected weight for proportional distribution
      let totalExpectedWeight = 0;
      const rollsByFabric: Record<string, { count: number; expectedWeight: number }> = {};

      bundleRolls.forEach((roll) => {
        const fabricCode = roll.expectedFabricCode || 'unknown';
        const expectedWeight = KnittingUtil.getRollWeightInLbs(roll) || roll.plannedWeight || 0;

        if (!rollsByFabric[fabricCode]) {
          rollsByFabric[fabricCode] = { count: 0, expectedWeight: 0 };
        }

        rollsByFabric[fabricCode].count += 1;
        rollsByFabric[fabricCode].expectedWeight += expectedWeight;
        totalExpectedWeight += expectedWeight;
      });

      // Distribute actual bundle weight proportionally
      if (totalExpectedWeight > 0) {
        Object.entries(rollsByFabric).forEach(([fabricCode, data]) => {
          const proportion = data.expectedWeight / totalExpectedWeight;
          const allocatedWeight = bundleWeight * proportion;

          if (!result[fabricCode]) {
            result[fabricCode] = 0;
          }
          result[fabricCode] += allocatedWeight;
        });
      }
    });

    return result;
  }

  getFabric(fabricCode: string): Fabric | undefined {
    return this.fabrics.find((f) => f.fabricCode === fabricCode);
  }

  getTotalPlannedBalance(): number {
    return this.productionCardPlannedBalances.reduce((sum, balance) => sum + balance.planned, 0);
  }

  getTotalCurrentBalance(): number {
    return this.productionCardPlannedBalances.reduce((sum, balance) => sum + balance.currentBalance, 0);
  }

  getTotalRollCount(): number {
    return this.productionCardPlannedBalances.reduce((sum, balance) => sum + balance.rollCount, 0);
  }

  getTotalBundleWeightBalance(): number {
    // Calculate total directly from weighed bundles (not per-fabric breakdown)
    // Since bundles weigh multiple rolls together, we can only show total weight
    return this.bundles
      .filter((bundle) => bundle.status >= ProductionCardBundleStatus.Weighed && (bundle.actualWeightInLbs ?? 0) > 0)
      .reduce((sum, bundle) => sum + (bundle.actualWeightInLbs ?? 0), 0);
  }

  // Finished goods specific methods
  get isFinishedGoodsMode(): boolean {
    return this.viewMode === 'finished-goods';
  }

  get finishedGoodsBalances(): FinishedGoodsBalance[] {
    const balancesMap: Record<string, FinishedGoodsBalance> = {};

    this.rolls.forEach((roll) => {
      const fabricCode = roll.expectedFabricCode!;
      const fabric = this.getFabric(fabricCode);
      const longDescription = fabric?.longDescription || 'Unknown Fabric';
      const type = !_.isNil(fabric?.fabricForm) ? this.fabricFormValues[fabric.fabricForm] : 'Unknown';
      const weight = roll.finalWeight?.weightInLbs || 0;
      const isLoaded = this.loadedRollNumbers.includes(roll.number!);

      if (!balancesMap[fabricCode]) {
        balancesMap[fabricCode] = {
          fabricCode,
          longDescription,
          type,
          totalRolls: 0,
          loadedRolls: 0,
          missingRolls: 0,
          totalWeight: 0,
          loadedWeight: 0,
        };
      }

      balancesMap[fabricCode].totalRolls += 1;
      balancesMap[fabricCode].totalWeight += weight;

      if (isLoaded) {
        balancesMap[fabricCode].loadedRolls += 1;
        balancesMap[fabricCode].loadedWeight += weight;
      } else {
        balancesMap[fabricCode].missingRolls += 1;
      }
    });

    return Object.values(balancesMap);
  }

  getFinishedGoodsBalanceStrict(item: unknown): FinishedGoodsBalance {
    return item as FinishedGoodsBalance;
  }

  getTotalFinishedGoodsRolls(): number {
    return this.finishedGoodsBalances.reduce((sum, balance) => sum + balance.totalRolls, 0);
  }

  getTotalLoadedRolls(): number {
    return this.finishedGoodsBalances.reduce((sum, balance) => sum + balance.loadedRolls, 0);
  }

  getTotalMissingRolls(): number {
    return this.finishedGoodsBalances.reduce((sum, balance) => sum + balance.missingRolls, 0);
  }

  getTotalFinishedGoodsWeight(): number {
    return this.finishedGoodsBalances.reduce((sum, balance) => sum + balance.totalWeight, 0);
  }

  getTotalLoadedWeight(): number {
    return this.finishedGoodsBalances.reduce((sum, balance) => sum + balance.loadedWeight, 0);
  }

  // Helper to check if a column is visible
  isColumnVisible(column: string): boolean {
    return this.visibleColumns.includes(column);
  }

  // UOM conversion methods
  convertWeight(weightInLbs: number, fabricCode?: string): number {
    switch (this.selectedUom) {
      case 'kg':
        return weightInLbs / 2.20462;
      case 'yd':
        if (fabricCode) {
          const fabric = this.getFabric(fabricCode);
          return fabric?.poundToYard ? weightInLbs * fabric.poundToYard : 0;
        }
        return 0;
      default:
        return weightInLbs;
    }
  }

  // For totals where we need to sum converted values per fabric
  getTotalPlannedBalanceConverted(): number {
    return this.productionCardPlannedBalances.reduce((sum, balance) => sum + this.convertWeight(balance.planned, balance.fabricCode), 0);
  }

  getTotalCurrentBalanceConverted(): number {
    return this.productionCardPlannedBalances.reduce(
      (sum, balance) => sum + this.convertWeight(balance.currentBalance, balance.fabricCode),
      0,
    );
  }

  getTotalBundleWeightBalanceConverted(): number {
    const statusForBundleWeight = [ProductionCardBundleStatus.Weighed, ProductionCardBundleStatus.BRD];
    return this.bundles.reduce((sum, bundle) => {
      if (statusForBundleWeight.includes(bundle.status) && (bundle.actualWeightInLbs ?? 0) > 0) {
        return sum + this.convertWeight(bundle.actualWeightInLbs ?? 0);
      }
      return sum;
    }, 0);
  }

  // Finished goods converted totals
  getTotalFinishedGoodsWeightConverted(): number {
    return this.finishedGoodsBalances.reduce((sum, balance) => sum + this.convertWeight(balance.totalWeight, balance.fabricCode), 0);
  }

  getTotalLoadedWeightConverted(): number {
    return this.finishedGoodsBalances.reduce((sum, balance) => sum + this.convertWeight(balance.loadedWeight, balance.fabricCode), 0);
  }

  showWarningExceedsIcon(balance: ProductionCardBalance) {
    return balance.planned > 0 && balance.currentBalance > balance.planned;
  }
}
